import {
    MessageSquareTextIcon,
    TrendingDownIcon,
    TrendingUpIcon,
    SmileIcon,
    FrownIcon,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { AiqmSentimentAggregate, SentimentComparison } from "@type/aiqm";
import { cn } from "@util/tailwind";
import { Tooltip } from "@component/tooltip";
import { Card, CardContent } from "@component/card";

function netLabel(score: number): string {
    return `${score > 0 ? "+" : ""}${score}`;
}

// Sentiment level → text/background treatment for the headline number.
function netTone(
    level: "positive" | "neutral" | "negative"
): { text: string; bg: string } {
    if (level === "positive") return { text: "text-emerald-700", bg: "bg-emerald-50" };
    if (level === "negative") return { text: "text-rose-700", bg: "bg-rose-50" };
    return { text: "text-gray-700", bg: "bg-gray-100" };
}

// Small "+12 vs last period" pill. Higher-is-better metrics colour positive
// deltas green; for a neutral metric (call count) we stay grey.
function DeltaPill({
    delta,
    colored = true,
    suffix = "vs last period",
}: {
    delta: number;
    colored?: boolean;
    suffix?: string;
}) {
    const Icon = delta > 0 ? TrendingUpIcon : delta < 0 ? TrendingDownIcon : TrendingUpIcon;
    const tone = !colored
        ? "text-gray-500"
        : delta > 0
          ? "text-emerald-600"
          : delta < 0
            ? "text-rose-600"
            : "text-gray-400";
    return (
        <span className={cn("inline-flex items-center gap-1 text-xs font-medium", tone)}>
            <Icon className="h-3 w-3" />
            {delta > 0 ? "+" : ""}
            {delta} {suffix}
        </span>
    );
}

interface KpiCardProps {
    label: string;
    value: string;
    valueClassName?: string;
    icon: LucideIcon;
    tooltip?: string;
    sub?: React.ReactNode;
}

function KpiCard({ label, value, valueClassName, icon: Icon, tooltip, sub }: KpiCardProps) {
    // Mirrors the shared training StatTile (dashboard/reports) — rounded blue
    // icon tile on the left, vertically centered, uppercase label, bold value,
    // hover affordance — plus an optional rich `sub` line below the value.
    const card = (
        <Card className="h-full transition-all duration-200 hover:border-blue-500 hover:shadow-md">
            <CardContent className="p-4 min-h-[64px]">
                <div className="flex items-center gap-3">
                    <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-lg bg-blue-50">
                        <Icon className="h-5 w-5 text-blue-600" />
                    </div>
                    <div className="min-w-0">
                        <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                            {label}
                        </div>
                        {/* Value + sub on one row to keep the card compact. Wraps the
                            sub below only when it can't fit beside the number. */}
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                            <div className={cn("text-2xl font-semibold text-gray-900 leading-none tabular-nums", valueClassName)}>
                                {value}
                            </div>
                            {sub && <div className="text-xs text-gray-500">{sub}</div>}
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );

    if (tooltip) {
        return <Tooltip content={<p>{tooltip}</p>}>{card}</Tooltip>;
    }
    return card;
}

interface SentimentKpiCardsProps {
    aggregate: AiqmSentimentAggregate;
    // Period-over-period figures (deltas + movers) — pre-computed by the BE.
    comparison?: SentimentComparison | null;
    callsAnalyzedTotal?: number;
}

export default function SentimentKpiCards({
    aggregate,
    comparison,
    callsAnalyzedTotal,
}: SentimentKpiCardsProps) {
    const net = aggregate.overall_net_sentiment;
    const level = aggregate.net_sentiment_level;
    const tone = netTone(level);

    // Period-over-period deltas — straight from the BE comparison block.
    const netDelta = comparison ? comparison.net_sentiment_delta : null;
    const callsDelta = comparison ? comparison.total_calls_delta : null;

    // Momentum (BE-computed). Bucket count only gates the "needs 2+ periods" state.
    const hasMomentum = aggregate.trend.length >= 2;
    const momentum = aggregate.momentum;
    // Use a directional icon even in the empty state (no momentum yet) — MinusIcon
    // reads as a missing/broken value rather than "pending data".
    const momentumIcon =
        momentum > 0 ? TrendingUpIcon : momentum < 0 ? TrendingDownIcon : TrendingUpIcon;
    const momentumTone =
        momentum > 0 ? "text-emerald-600" : momentum < 0 ? "text-rose-600" : "text-gray-400";

    // Theme split (BE-computed): how many themes skew good vs bad.
    const split = aggregate.theme_split;
    const displayedCallsTotal = callsAnalyzedTotal ?? aggregate.total_calls;
    const callsCountAligned = displayedCallsTotal === aggregate.total_calls;

    return (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <KpiCard
                label="Overall sentiment"
                value={netLabel(net)}
                valueClassName={tone.text}
                icon={net >= 0 ? SmileIcon : FrownIcon}
                tooltip="Net sentiment across all calls (−100 to +100)."
                sub={
                    netDelta !== null ? (
                        <DeltaPill delta={netDelta} />
                    ) : (
                        <span
                            className={cn(
                                "rounded px-1.5 py-0.5 text-[11px] font-medium",
                                tone.bg,
                                tone.text
                            )}
                        >
                            {level === "positive"
                                ? "Net positive"
                                : level === "negative"
                                  ? "Net negative"
                                  : "Neutral"}
                        </span>
                    )
                }
            />

            <KpiCard
                label="Calls analyzed"
                value={displayedCallsTotal.toLocaleString()}
                icon={MessageSquareTextIcon}
                tooltip="Calls analyzed in the selected range & filters."
                sub={
                    callsCountAligned && callsDelta !== null ? (
                        <DeltaPill delta={callsDelta} colored={false} />
                    ) : !callsCountAligned ? (
                        "aligned to Calls tab (results ready)"
                    ) : (
                        `across ${aggregate.trend.length} period${aggregate.trend.length === 1 ? "" : "s"}`
                    )
                }
            />

            <KpiCard
                label="Momentum"
                value={hasMomentum ? `${momentum > 0 ? "+" : ""}${momentum}` : "—"}
                valueClassName={momentumTone}
                icon={momentumIcon}
                tooltip="Sentiment change over the latest period, using the time grouping selected on the trend chart (daily / weekly / monthly)."
                sub={
                    hasMomentum
                        ? momentum > 0
                            ? "improving vs last period"
                            : momentum < 0
                              ? "declining vs last period"
                              : "flat vs last period"
                        : "needs 2+ periods"
                }
            />

            <KpiCard
                label="Theme split"
                value={`${split.positive} / ${split.negative}`}
                icon={SmileIcon}
                tooltip="Themes skewing positive vs negative."
                sub={
                    <span className="inline-flex items-center gap-2">
                        <span className="inline-flex items-center gap-1 text-emerald-600">
                            <span className="h-2 w-2 rounded-full bg-emerald-400" />
                            {split.positive} positive
                        </span>
                        <span className="inline-flex items-center gap-1 text-rose-600">
                            <span className="h-2 w-2 rounded-full bg-rose-400" />
                            {split.negative} negative
                        </span>
                    </span>
                }
            />
        </div>
    );
}
